import { useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import {
  useGetAuthoringComponentRevisionQuery,
  useListSubmissionsQuery,
  type SubmissionSummary,
} from '../../api/authoring'
import { formFields, submitterOf, subjectOf } from '../../model/form'
import { routes } from '../../model/routes'
import { Loading, QueryError } from '../../ui/QueryState'
import { statusLabel, statusColour, when } from './submissionStatus'

/**
 * What visitors sent through this site's forms.
 *
 * **The default view is enquiries, and filtered submissions are one tab
 * away.** The two counts are stated apart and never summed, so the
 * default answer to "has anybody contacted me" stays the clean one.
 *
 * The reason is not that spam is secret. Showing it in the same list
 * hands back the work the filter was doing: the filter's value to a
 * customer is *"I don't have to look at this"*, and a table that lists
 * spam beside enquiries protects their inbox and not their attention.
 *
 * **Filtered is a tab, not a permission.** Nothing is withheld and no
 * role gates it — a customer can always reach a submission a defence
 * refused and rescue it, which is the whole reason the row is kept. The
 * separation decides what the default view answers; it never decides what
 * the customer may see.
 */
export function Enquiries() {
  const { tenant = '', site = '' } = useParams()
  const [filtered, setFiltered] = useState(false)
  // The cursor stack, rather than a page number. The listing is
  // cursor-paged and the cursor is opaque, so "back" is a position this
  // screen remembers rather than one it can compute.
  const [trail, setTrail] = useState<string[]>([])
  const cursor = trail[trail.length - 1]

  const page = useListSubmissionsQuery({ tenant, website: site, filtered, cursor })
  // The other tab's first page, read only for its count. It is the cheap
  // half of stating the two numbers apart, and it is why the count beside
  // a tab is "at least this many" rather than a total: a page is bounded
  // and a full one says so.
  const other = useListSubmissionsQuery({ tenant, website: site, filtered: !filtered })

  const changeTab = (next: boolean) => {
    setFiltered(next)
    setTrail([])
  }

  if (page.isLoading && !page.data) return <Loading label="Reading enquiries…" />
  if (page.error) return <QueryError error={page.error} title="Could not read enquiries" />

  const rows = page.data?.submissions ?? []
  const otherCount = other.data?.submissions.length ?? 0

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h1">Enquiries</Typography>
        <Typography color="text.secondary">
          Everything visitors sent through this site&rsquo;s forms.
        </Typography>
      </Box>

      {/* **It says what it is.** A limited retention means the screen
          empties behind the reader, and a table that quietly stops
          showing last month's enquiries reads as data loss. So the
          window is stated and so is the fact that the email is the
          durable copy — the row is a receipt, not an archive. */}
      <Alert severity="info">
        Enquiries are kept here for a limited time and then deleted. The email you were sent when
        each one arrived is the copy that lasts &mdash; this list is a receipt, not an archive.
      </Alert>

      <Paper>
        <Tabs value={filtered ? 1 : 0} onChange={(_event, value) => changeTab(value === 1)}>
          <Tab label={countLabel('Enquiries', filtered ? otherCount : rows.length)} />
          <Tab label={countLabel('Filtered', filtered ? rows.length : otherCount)} />
        </Tabs>

        {filtered && (
          <Alert severity="warning" square>
            These were stopped before anyone was emailed &mdash; by the spam filter, or by the
            hidden field a real visitor never fills in. They are shown as plain text and nothing in
            them is a link. If one is a real enquiry, open it and send it.
          </Alert>
        )}

        {rows.length === 0 ? (
          <Box sx={{ p: 4 }}>
            <Typography color="text.secondary">
              {filtered ? 'Nothing has been filtered.' : 'Nobody has sent anything yet.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>About</TableCell>
                  <TableCell>Form</TableCell>
                  <TableCell>What happened</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((submission) => (
                  <EnquiryRow
                    key={submission.id}
                    tenant={tenant}
                    site={site}
                    submission={submission}
                    inert={filtered}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* **The cursor says whether the listing continues, and the row
            count never does.** Narrowing by tab is applied after a page
            is read, so a page can come back short while more rows still
            exist — a reader who stopped on a short page would stop
            early. */}
        <Stack direction="row" spacing={1} sx={{ p: 2 }} justifyContent="flex-end">
          <Button disabled={trail.length === 0} onClick={() => setTrail(trail.slice(0, -1))}>
            Newer
          </Button>
          <Button
            disabled={!page.data?.cursor}
            onClick={() => page.data?.cursor && setTrail([...trail, page.data.cursor])}
          >
            Older
          </Button>
        </Stack>
      </Paper>
    </Stack>
  )
}

/**
 * One row, with the form it answers read at the revision the visitor
 * answered.
 *
 * A component of its own so the read is a hook: the revision is pinned
 * and immutable, so the client caches it forever and a page of twenty
 * rows over three forms makes three requests rather than twenty. Reading
 * *this* revision rather than the current one is what stops a form edited
 * since from labelling last month's answers wrongly.
 */
function EnquiryRow({
  tenant,
  site,
  submission,
  inert,
}: {
  tenant: string
  site: string
  submission: SubmissionSummary
  inert: boolean
}) {
  const form = useGetAuthoringComponentRevisionQuery({
    tenant,
    id: submission.form,
    revision: submission.revision,
  })
  const fields = formFields(form.data)
  const values = submission.values ?? {}
  const from = submitterOf(fields, values)
  const about = subjectOf(fields, values)

  return (
    <TableRow hover>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Link component={RouterLink} to={routes.enquiry(tenant, site, submission.id)}>
          {when(submission.acceptedAt)}
        </Link>
      </TableCell>
      {/* Plain text in both tabs. The "from" and "about" columns are
          whatever somebody typed into a public form, and a table that
          linkified them would be doing it for the filtered tab too. */}
      <TableCell>{from ?? <Unstated />}</TableCell>
      <TableCell sx={{ maxWidth: 360 }}>
        <Typography noWrap title={inert ? undefined : about}>
          {about ?? <Unstated />}
        </Typography>
      </TableCell>
      <TableCell>{form.data?.label ?? '—'}</TableCell>
      <TableCell>
        <Stack direction="row" spacing={1}>
          <Chip size="small" label={statusLabel(submission.status)} color={statusColour(submission.status)} />
          {submission.judgement && (
            <Chip
              size="small"
              variant="outlined"
              label={submission.judgement.verdict === 'spam' ? 'Marked spam' : 'Marked genuine'}
            />
          )}
        </Stack>
      </TableCell>
    </TableRow>
  )
}

/** A form that declares no such role has no answer, and says so. */
function Unstated() {
  return <Typography component="span" color="text.disabled">Not asked for</Typography>
}

function countLabel(label: string, count: number) {
  return count === 0 ? label : `${label} (${count})`
}
